import { useCallback } from 'react';
import type { ChildResult } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { mergeChildResults } from '@atlassian/jira-command-palette/src/common/utils.tsx';
import { useNavigationViewPlansPermission } from '@atlassian/jira-plans-item-visibility/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { QuickSearchResponseData } from '../../../../common/types.tsx';
import { useGetErrorChildResult } from '../../../../common/ui/error/index.tsx';
import { useGetRecommendedChildResult } from '../../../../common/ui/recommended/index.tsx';
import { useSetTimeoutAutoClear } from '../../../../common/utils/use-set-timeout-auto-clear/index.tsx';
import { transformResultsForAnalytics } from '../../../../controllers/use-quick-search-analytics/utils.tsx';
import { useGetSearchBoardsChildResult } from './boards/index.tsx';
import { useGetSearchFiltersChildResult } from './filters/index.tsx';
import { useGetSearchIssuesChildResult } from './issues/index.tsx';
import { useGetSearchPlansChildResult } from './plans/index.tsx';
import { useGetSearchProjectsChildResult } from './projects/index.tsx';

export const useGetSearchResults = () => {
	const { getChildResult: getErrorChildResult } = useGetErrorChildResult();
	const { getChildResult: getRecommendedChildResult } = useGetRecommendedChildResult();
	const { getChildResult: getSearchIssuesChildResult } = useGetSearchIssuesChildResult();
	const { getChildResult: getSearchProjectsChildResult } = useGetSearchProjectsChildResult();
	const { getChildResult: getSearchBoardsChildResult } = useGetSearchBoardsChildResult();
	const { getChildResult: getSearchFiltersChildResult } = useGetSearchFiltersChildResult();
	const { getChildResult: getSearchPlansChildResult } = useGetSearchPlansChildResult();

	const [hasEditOrViewPermissions] = useNavigationViewPlansPermission();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { setTimeoutAutoClear } = useSetTimeoutAutoClear();

	const setTimeoutFunc = setTimeoutAutoClear;

	const getSearchResults = useCallback(
		({
			search,
			quickSearchResponseData,
			error,
		}: {
			search: string;
			quickSearchResponseData?: QuickSearchResponseData;
			error?: Error;
		}): ChildResult => {
			setTimeoutFunc(() => {
				fireUIAnalytics(createAnalyticsEvent({}), 'searchResults shown', 'postQuerySearchResults', {
					...transformResultsForAnalytics(quickSearchResponseData, false),
				});
			}, 0);

			return mergeChildResults([
				getErrorChildResult({ search, quickSearchResponseData, error }),
				getRecommendedChildResult({ search, quickSearchResponseData, error }),
				getSearchIssuesChildResult({ search, quickSearchResponseData, error }),
				getSearchProjectsChildResult({ search, quickSearchResponseData, error }),
				getSearchBoardsChildResult({ search, quickSearchResponseData, error }),
				getSearchFiltersChildResult({ search, quickSearchResponseData, error }),
				...(hasEditOrViewPermissions
					? [getSearchPlansChildResult({ search, quickSearchResponseData, error })]
					: []),
			]);
		},
		[
			createAnalyticsEvent,
			getErrorChildResult,
			getRecommendedChildResult,
			getSearchBoardsChildResult,
			getSearchFiltersChildResult,
			getSearchIssuesChildResult,
			getSearchPlansChildResult,
			getSearchProjectsChildResult,
			hasEditOrViewPermissions,
			setTimeoutFunc,
		],
	);

	return getSearchResults;
};
